import {
  X, Calendar, MapPin, DollarSign, Clock, Mail, Phone, User, Users,
  FileText, Loader, ExternalLink, ArrowUpRight, ArrowDownRight, Edit,
  RefreshCw, Trash2, Ban, CreditCard,
} from "lucide-react";
import { getStatusBadge, formatDate, formatCurrency } from "./jobConstants";
import { jobStatus, badgeStyle } from "@/utils/szStatus";

const avatarColors = ["#572B7C", "#78287C", "#28C0D0", "#0F766E", "#2563EB", "#15803D", "#D97706"];
const colorFor = (name) => {
  const code = (name || "").split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return avatarColors[code % avatarColors.length];
};
const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
};

const PersonAvatar = ({ name, photoUrl, size = 40 }) => (
  <div
    className="rounded-full flex items-center justify-center text-white font-bold overflow-hidden shrink-0"
    style={{ width: size, height: size, background: colorFor(name), fontFamily: "Poppins", fontSize: size >= 40 ? 13 : 11 }}
  >
    {photoUrl ? <img src={photoUrl} alt="" className="w-full h-full object-cover" /> : <span>{getInitials(name)}</span>}
  </div>
);

const Overline = ({ children, className = "" }) => (
  <div className={`sz-overline text-[#8A90A2] ${className}`}>{children}</div>
);

const JobDetailsModal = ({
  viewingJob, jobDetails, loadingDetails,
  transactionHistory, loadingTransactions,
  detailsTab, onTabChange, onClose,
  onEdit, onChangeStatus, onAuditLog, onCancelHiredJob, onCancelCrewMember,
  onSendFollowup, sendingFollowup,
}) => {
  if (!viewingJob) return null;

  const stop = (e) => e.stopPropagation();
  const job = jobDetails?.job;
  const st = jobStatus(job?.status || viewingJob.status);

  const totalPeople = (jobDetails?.applicants?.length || 0) + (jobDetails?.hired_crew?.length || 0) + (jobDetails?.owner ? 1 : 0);
  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "people", label: "People", count: totalPeople },
    { id: "payments", label: "Payments" },
  ];

  const idLabel = viewingJob.mysql_job_id ? `#${viewingJob.mysql_job_id}` : viewingJob.id?.substring(0, 8);
  const metaParts = job
    ? [job.owner?.name || jobDetails?.owner?.name, job.location_address || job.location, job.date && new Date(job.date).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })].filter(Boolean)
    : [];

  return (
    <div className="sz-modal-backdrop" style={{ zIndex: 100 }} onClick={onClose}>
      <div className="sz-modal-card w-full max-w-[760px] overflow-hidden" onClick={stop}>
        {/* Dark header */}
        <div className="p-[22px_24px] text-white shrink-0" style={{ background: "linear-gradient(120deg,#0C1120,#2B1840)" }}>
          <div className="flex items-start justify-between gap-3.5">
            <div className="min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <span className="sz-badge sz-nodot" style={badgeStyle(st)}>{st.label}</span>
                <span className="text-[11px]" style={{ fontFamily: "Poppins", color: "rgba(255,255,255,.7)" }}>{idLabel}</span>
              </div>
              <h3 className="sz-h3 mt-2.5" style={{ fontSize: 20, color: "#fff" }}>{viewingJob.title}</h3>
              {metaParts.length > 0 && (
                <div className="text-[12.5px] mt-1" style={{ color: "rgba(255,255,255,.72)" }}>{metaParts.join(" · ")}</div>
              )}
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-[9px] flex items-center justify-center text-white shrink-0" style={{ background: "rgba(255,255,255,.16)" }}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        {jobDetails && (
          <div className="flex gap-0.5 px-4 shrink-0" style={{ borderBottom: "1px solid #EEF0F4" }}>
            {tabs.map((tab) => {
              const active = detailsTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => onTabChange(tab.id)}
                  className="px-4 py-[13px] text-[13px] font-semibold flex items-center gap-1.5"
                  style={{ color: active ? "#572B7C" : "#8A90A2", borderBottom: `2px solid ${active ? "#572B7C" : "transparent"}` }}
                >
                  {tab.label}
                  {tab.count !== undefined && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded-full" style={{ background: "#F1F3F7", color: "#8A90A2" }}>{tab.count}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Content */}
        <div className="szscroll flex-1 overflow-y-auto p-[20px_24px]">
          {loadingDetails ? (
            <div className="flex items-center justify-center py-12"><Loader className="w-8 h-8 animate-spin text-[#8A90A2]" /></div>
          ) : jobDetails ? (
            <>
              {/* OVERVIEW */}
              {detailsTab === "overview" && (
                <div className="space-y-5">
                  {job.status === "CANCELLED" && (
                    <div className="rounded-[14px] p-4" style={{ background: "#FEE2E2", border: "1px solid #FECACA" }}>
                      <div className="flex items-start gap-3">
                        <Ban className="w-5 h-5 text-[#B91C1C] shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                            <h4 className="text-sm font-bold text-[#B91C1C]">Job Cancelled</h4>
                            {job.cancelled_at && (
                              <span className="text-xs text-[#B91C1C]">
                                {new Date(job.cancelled_at).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                                {job.cancelled_by && <span style={{ color: "rgba(185,28,28,.7)" }}> · by {job.cancelled_by}</span>}
                              </span>
                            )}
                          </div>
                          {job.cancellation_reason && (
                            <div className="mb-2">
                              <span className="sz-overline text-[#B91C1C]">Reason</span>
                              <p className="text-sm text-[#7F1D1D]">{job.cancellation_reason}</p>
                            </div>
                          )}
                          {job.cancellation_notes ? (
                            <div>
                              <span className="sz-overline text-[#B91C1C]">Notes</span>
                              <p className="text-sm text-[#7F1D1D] whitespace-pre-wrap">{job.cancellation_notes}</p>
                            </div>
                          ) : (
                            <p className="text-xs italic" style={{ color: "rgba(185,28,28,.7)" }}>No cancellation notes recorded.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 3-col field grid */}
                  <div className="grid gap-x-[22px] gap-y-4" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
                    <Field label="Date" value={
                      job.dates?.length > 0
                        ? job.dates.map((d) => new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "short" })).join(", ")
                        : job.date ? new Date(job.date).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "TBD"
                    } />
                    <Field label="Location" value={job.location_address || job.location || "TBD"} />
                    <Field label="Budget" value={formatCurrency(job.budget || job.total_amount || 0)} />
                    <Field label="Duration" value={
                      job.hours ? `${job.hours} hours`
                        : job.crew_members?.length > 0 ? `${job.crew_members.reduce((s, m) => s + (m.hours || 0), 0)} hours`
                        : job.startTime && job.endTime ? `${job.startTime} – ${job.endTime}`
                        : job.start_time && job.end_time ? `${job.start_time} – ${job.end_time}` : "TBD"
                    } />
                    <Field label="Applications" value={String(jobDetails.applicants?.length || job.applications || 0)} />
                    <Field label="Posted by" value={jobDetails.owner?.name || job.owner?.name || "—"} />
                    {(job.role || job.category) && <Field label="Role" value={job.role || job.category} />}
                    {job.hourly_rate && <Field label="Rate" value={`${formatCurrency(job.hourly_rate)}/hr`} />}
                  </div>

                  {/* Crew roles required */}
                  {job.crew_members?.length > 0 && (
                    <div>
                      <Overline className="mb-2.5">Crew roles</Overline>
                      <div className="flex flex-col gap-2">
                        {job.crew_members.map((member, idx) => (
                          <div key={idx} className="rounded-[11px] p-[11px_14px]" style={{ background: "#FAFBFC", border: "1px solid #EEF0F4" }}>
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="text-[13px] font-semibold capitalize">{member.role?.replace(/_/g, " ") || "Role"}</span>
                                <span className="text-xs text-[#8A90A2] ml-2">{member.hours || "?"}h × {formatCurrency(member.ratePerHour || 0)}/hr</span>
                              </div>
                              <span className="text-[13px] font-bold" style={{ fontFamily: "Poppins" }}>{formatCurrency(member.subtotal || (member.hours * member.ratePerHour) || 0)}</span>
                            </div>
                            {member.dateAssignments?.filter((a) => a.hours > 0).length > 0 && (
                              <div className="mt-1.5 pl-1 space-y-0.5">
                                {member.dateAssignments.filter((a) => a.hours > 0).map((a, i) => (
                                  <div key={i} className="flex items-center gap-1.5 text-[11px] text-[#8A90A2]">
                                    <Calendar className="w-3 h-3" /><span>{formatDate(a.date)}</span><span>—</span><span className="font-medium">{a.hours} hrs</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {job.description && (
                    <div>
                      <Overline className="mb-2">Description</Overline>
                      <p className="text-[13px] text-[#2B3142] whitespace-pre-wrap rounded-[11px] p-4" style={{ background: "#FAFBFC", border: "1px solid #EEF0F4" }}>{job.description}</p>
                    </div>
                  )}

                  {(job.notes || job.special_requirements) && (
                    <div>
                      <Overline className="mb-2">Additional Notes</Overline>
                      <div className="rounded-[11px] p-4" style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}>
                        <p className="text-[13px] text-[#92400E] whitespace-pre-wrap">{job.notes || job.special_requirements}</p>
                      </div>
                    </div>
                  )}

                  {job.stripe_payment_intent_id && (
                    <p className="text-xs text-[#8A90A2] sz-mono">Stripe PI: {job.stripe_payment_intent_id}</p>
                  )}
                </div>
              )}

              {/* PEOPLE */}
              {detailsTab === "people" && (
                <div className="space-y-5">
                  {/* Owner */}
                  <div>
                    <Overline className="mb-2.5">Owner</Overline>
                    {jobDetails.owner ? (
                      <div className="flex items-center gap-3 p-[12px_14px] rounded-[12px]" style={{ background: "#FAFBFC", border: "1px solid #EEF0F4" }}>
                        <PersonAvatar name={jobDetails.owner.name} photoUrl={jobDetails.owner.profile_photo_url} />
                        <div className="min-w-0 flex-1">
                          <div className="text-[13.5px] font-semibold">{jobDetails.owner.name || "Unknown"}</div>
                          <div className="flex items-center gap-3 text-xs text-[#8A90A2] mt-0.5 flex-wrap">
                            {jobDetails.owner.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{jobDetails.owner.email}</span>}
                            {jobDetails.owner.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{jobDetails.owner.phone}</span>}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Empty icon={<User className="w-8 h-8" />} text="Owner not found" />
                    )}
                  </div>

                  {/* Hired crew */}
                  <div>
                    <Overline className="mb-2.5">Hired crew · {jobDetails.hired_crew?.length || 0}</Overline>
                    {jobDetails.hired_crew?.length > 0 ? (
                      <div className="flex flex-col gap-2">
                        {jobDetails.hired_crew.map((crew, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-[11px_14px] rounded-[12px] bg-white" style={{ border: "1px solid #EEF0F4" }}>
                            <PersonAvatar name={crew.name || crew.full_name} photoUrl={crew.profile_photo_url} size={38} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[13px] font-semibold">{crew.name || crew.full_name || "Unknown"}</span>
                                {crew.role && <span className="text-[10px] px-1.5 py-0.5 rounded-full capitalize" style={{ color: "#78287C", background: "#EDE4F4" }}>{crew.role.replace(/_/g, " ")}</span>}
                              </div>
                              <div className="text-[11.5px] text-[#8A90A2] truncate">{crew.role ? "" : ""}{crew.email}</div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {crew.payout && <span className="text-[13px] font-bold text-[#15803D]" style={{ fontFamily: "Poppins" }}>{formatCurrency(crew.payout)}</span>}
                              {crew.user_id && crew.crew_member_index !== undefined && ["HIRED", "PARTIALLY_HIRED", "PENDING_COMPLETION", "RELEASE_PAYMENT"].includes(viewingJob?.status) && (
                                <button
                                  onClick={() => onCancelCrewMember?.({
                                    userId: crew.user_id, name: crew.name || crew.full_name || "Unknown",
                                    email: crew.email, phone: crew.phone, photo: crew.profile_photo_url,
                                    role: crew.role, subtotal: crew.subtotal || crew.payout, originalIndex: crew.crew_member_index,
                                  }, crew.crew_member_index)}
                                  className="text-xs text-[#DC2626] font-semibold px-2 py-1 rounded hover:bg-[#FEF2F2]"
                                  title="Cancel this position (no-show)"
                                >Cancel (No-Show)</button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Empty icon={<Users className="w-8 h-8" />} text="No crew hired yet" />
                    )}
                  </div>

                  {/* Applicants */}
                  <div>
                    <Overline className="mb-2.5">Applicants · {jobDetails.applicants?.length || 0}</Overline>
                    {jobDetails.applicants?.length > 0 ? (
                      <div className="flex flex-col gap-2 max-h-[360px] overflow-y-auto szscroll">
                        {jobDetails.applicants.map((app, idx) => (
                          <div key={idx} className="p-[11px_14px] rounded-[12px] bg-white" style={{ border: "1px solid #EEF0F4" }}>
                            <div className="flex items-start gap-3">
                              <PersonAvatar name={app.name || app.full_name} photoUrl={app.profile_photo_url} size={38} />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-[13px] font-semibold">{app.name || app.full_name || "Unknown"}</span>
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={
                                    app.status === "accepted" ? { color: "#15803D", background: "#DCFCE7" }
                                    : app.status === "rejected" ? { color: "#B91C1C", background: "#FEE2E2" }
                                    : app.status === "offer_sent" ? { color: "#B45309", background: "#FEF3C7" }
                                    : { color: "#8A90A2", background: "#F1F3F7" }
                                  }>{app.status || "pending"}</span>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-[#8A90A2] mt-0.5 flex-wrap">
                                  {app.email && <span>{app.email}</span>}
                                  {app.applied_at && <span>Applied {new Date(app.applied_at).toLocaleDateString("en-AU")}</span>}
                                </div>
                                {app.message && <p className="text-xs text-[#8A90A2] italic mt-1.5 line-clamp-2">{app.message}</p>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Empty icon={<Users className="w-8 h-8" />} text="No applicants yet" />
                    )}
                  </div>
                </div>
              )}

              {/* PAYMENTS */}
              {detailsTab === "payments" && (
                <div className="space-y-5">
                  {(job.status === "COMPLETED" || job.completed_at || job.payment_processed_at) && (
                    <div>
                      <Overline className="mb-2.5">Completion Status</Overline>
                      <div className="grid grid-cols-2 gap-3">
                        {job.completed_at && (
                          <div className="rounded-[11px] p-3" style={{ background: "#DCFCE7", border: "1px solid #BBF7D0" }}>
                            <p className="text-xs text-[#15803D]">Completed On</p>
                            <p className="text-sm font-semibold text-[#166534]">{new Date(job.completed_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                          </div>
                        )}
                        {job.payment_processed_at && (
                          <div className="rounded-[11px] p-3" style={{ background: "#DBEAFE", border: "1px solid #BFDBFE" }}>
                            <p className="text-xs text-[#2563EB]">Payment Released</p>
                            <p className="text-sm font-semibold text-[#1D4ED8]">{new Date(job.payment_processed_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Line-item breakdown */}
                  {jobDetails.payment_breakdown?.base_payment > 0 && (
                    <div className="rounded-[14px] p-[16px_18px]" style={{ background: "#FAFBFC", border: "1px solid #EEF0F4" }}>
                      <PayLine label="Base payment" value={formatCurrency(jobDetails.payment_breakdown.base_payment)} />
                      {jobDetails.payment_breakdown.extra_payment_amount > 0 && (
                        <>
                          <PayLine label="Extra payment" value={`+${formatCurrency(jobDetails.payment_breakdown.extra_payment_amount)}`} valFg="#15803D" />
                          {jobDetails.payment_breakdown.extra_payment_reason && (
                            <PayLine label="Reason" value={jobDetails.payment_breakdown.extra_payment_reason} small italic />
                          )}
                        </>
                      )}
                      <PayLine label="Job total" value={formatCurrency(jobDetails.payment_breakdown.total_job_amount)} weight="600" />
                      <PayLine label="Platform fee (2%)" value={`−${formatCurrency(jobDetails.payment_breakdown.platform_fee)}`} labelFg="#B91C1C" valFg="#B91C1C" />
                      <PayLine label="Crew payout" value={formatCurrency(jobDetails.payment_breakdown.crew_payout)} weight="700" size="15px" valFg="#15803D" last />
                    </div>
                  )}

                  {/* Crew banking */}
                  {jobDetails.crew_banking && (
                    <div>
                      <Overline className="mb-2.5">Crew Banking Details</Overline>
                      {jobDetails.crew_banking.has_details ? (
                        <div className="rounded-[12px] p-4" style={{ background: "#CFFAFE", border: "1px solid #A5F3FC" }}>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div><p className="text-xs text-[#0E7490]">Bank Name</p><p className="font-medium">{jobDetails.crew_banking.bank_name || "N/A"}</p></div>
                            <div><p className="text-xs text-[#0E7490]">Account Name</p><p className="font-medium">{jobDetails.crew_banking.account_name || "N/A"}</p></div>
                            <div><p className="text-xs text-[#0E7490]">BSB</p><p className="sz-mono font-medium">{jobDetails.crew_banking.bsb}</p></div>
                            <div><p className="text-xs text-[#0E7490]">Account Number</p><p className="sz-mono font-medium">{jobDetails.crew_banking.account_number}</p></div>
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-[11px] p-3" style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}>
                          <p className="text-sm text-[#92400E]">No banking details on file for crew member</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Transaction history */}
                  <div>
                    <Overline className="mb-2.5">Transaction history</Overline>
                    {loadingTransactions ? (
                      <div className="flex items-center justify-center py-6"><Loader className="w-5 h-5 animate-spin text-[#572B7C]" /><span className="ml-2 text-sm text-[#8A90A2]">Loading transactions…</span></div>
                    ) : transactionHistory.length > 0 ? (
                      <div className="flex flex-col gap-2">
                        {transactionHistory.map((txn, idx) => (
                          <div key={idx} className="flex items-center justify-between p-[10px_14px] rounded-[11px] bg-white" style={{ border: "1px solid #EEF0F4" }}>
                            <div className="flex items-center gap-2.5">
                              {txn.type === "charge" || txn.type === "payment" ? <ArrowDownRight className="w-4 h-4 text-[#15803D]" />
                                : txn.type === "refund" ? <ArrowUpRight className="w-4 h-4 text-[#B91C1C]" />
                                : <CreditCard className="w-4 h-4 text-[#8A90A2]" />}
                              <div>
                                <div className="text-[12.5px] font-semibold capitalize">
                                  {txn.type || "Transaction"}
                                  {txn.status && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={
                                    txn.status === "succeeded" ? { color: "#15803D", background: "#DCFCE7" }
                                    : txn.status === "pending" ? { color: "#B45309", background: "#FEF3C7" }
                                    : txn.status === "failed" ? { color: "#B91C1C", background: "#FEE2E2" }
                                    : { color: "#8A90A2", background: "#F1F3F7" }
                                  }>{txn.status}</span>}
                                </div>
                                <div className="text-[11px] text-[#8A90A2] sz-mono">{txn.created_at ? new Date(txn.created_at * 1000).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "N/A"}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-[13px] font-bold" style={{ fontFamily: "Poppins", color: txn.type === "refund" ? "#B91C1C" : "#15803D" }}>{txn.type === "refund" ? "−" : ""}${((txn.amount || 0) / 100).toFixed(2)}</div>
                              {txn.id && (
                                <a href={`https://dashboard.stripe.com/payments/${txn.payment_intent_id || txn.id}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#572B7C] flex items-center gap-0.5 justify-end">
                                  {txn.id.substring(0, 18)}…<ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-[11px] p-4" style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}>
                        <p className="text-sm font-medium text-[#92400E]">No Stripe payment linked</p>
                        <p className="text-xs text-[#B45309] mt-1">This job was created before Stripe integration or was migrated from the old system.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-[#8A90A2]">Failed to load job details</div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2.5 p-[14px_24px] shrink-0 flex-wrap" style={{ borderTop: "1px solid #EEF0F4" }}>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => onAuditLog(viewingJob.id)} className="sz-btn sz-btn-outline"><FileText className="w-4 h-4" />Audit Log</button>
            <button onClick={() => onChangeStatus(viewingJob)} className="sz-btn sz-btn-outline"><RefreshCw className="w-4 h-4" />Status</button>
            {job?.status === "HIRED" && (
              <button onClick={() => onCancelHiredJob(viewingJob)} className="sz-btn sz-btn-outline" style={{ borderColor: "#FECACA", color: "#DC2626" }}><Trash2 className="w-4 h-4" />Cancel</button>
            )}
            {job?.status === "SEEKING_APPLICANTS" && (
              <button onClick={onSendFollowup} disabled={sendingFollowup} className="sz-btn sz-btn-outline">
                {sendingFollowup ? <Loader className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}Followup
              </button>
            )}
          </div>
          <div className="flex items-center gap-2.5">
            <button onClick={onClose} className="sz-btn sz-btn-outline">Close</button>
            <button onClick={() => onEdit(viewingJob)} className="sz-btn sz-btn-primary"><Edit className="w-4 h-4" />Edit Job</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <div className="sz-overline text-[#8A90A2] mb-1">{label}</div>
    <div className="text-[13.5px] text-[#0C1120] font-medium break-words">{value}</div>
  </div>
);

const Empty = ({ icon, text }) => (
  <div className="rounded-[12px] p-6 text-center" style={{ background: "#FAFBFC", border: "1px solid #EEF0F4" }}>
    <div className="text-[#A8AEBC] flex justify-center mb-2">{icon}</div>
    <p className="text-sm text-[#8A90A2]">{text}</p>
  </div>
);

const PayLine = ({ label, value, labelFg = "#2B3142", valFg = "#0C1120", weight = "500", size = "13px", small, italic, last }) => (
  <div className="flex items-center justify-between py-[9px]" style={{ borderBottom: last ? "none" : "1px solid #EEF0F4" }}>
    <span className={`${small ? "text-xs" : "text-[13px]"} ${italic ? "italic" : ""}`} style={{ color: small ? "#8A90A2" : labelFg, fontWeight: weight }}>{label}</span>
    <span style={{ fontFamily: "Poppins", fontWeight: weight, fontSize: size, color: valFg }}>{value}</span>
  </div>
);

export default JobDetailsModal;
